"use client";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import InputPasswordWithReveal from "@/components/customs/InputPasswordWithReveal";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import Image from "next/image";
import useRegister, { PhoneFormValues } from "./useRegister";
import getSelectedCountry from "@/lib/getSelectedCountry";
import lang from "./lang.json";
import { SupportedLanguage } from "@/lib/shared";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { localizedPath } from "@/lib/localePath";

function FormRegisterWithPhone({ locale }: { locale: SupportedLanguage }) {
  const { form, onSubmit, isLoading } = useRegister({ tab: "phone" });

  const typedForm = form as UseFormReturn<PhoneFormValues>;

  const countrySelected = getSelectedCountry(
    !!typedForm.watch("phone") ? (typedForm.watch("phone") as string) : ""
  );

  const handleSubmit: SubmitHandler<PhoneFormValues> = (data) => {
    return (onSubmit as (values: PhoneFormValues) => Promise<void>)(data);
  };

  return (
    <Form {...typedForm}>
      <form
        onSubmit={typedForm.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={typedForm.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["role"][locale]}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Personal" id="Personal" />
                      <Label htmlFor="Personal">
                        {lang["role-personal"][locale]}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Company" id="Company" />
                      <Label htmlFor="Company">
                        {lang["role-company"][locale]}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["fullname"][locale]}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={lang["placeholder-fullname"][locale]}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["phone"][locale]}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    {countrySelected?.image ? (
                      <span className="relative rounded-full overflow-hidden w-7 aspect-square">
                        <Image
                          src={countrySelected.image}
                          alt={countrySelected.name}
                          className="scale-200"
                          fill
                        />
                      </span>
                    ) : (
                      <span className="rounded-full overflow-hidden w-7 aspect-square bg-gray-300"></span>
                    )}
                  </span>
                  <Input
                    placeholder="Enter your phone number start with +"
                    className="pl-12"
                    {...field}
                    value={field.value as string}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <InputPasswordWithReveal
          form={typedForm}
          name="password"
          label={lang["password"][locale]}
          placeholder={lang["placeholder-password"][locale]}
        />

        <InputPasswordWithReveal
          form={typedForm}
          name="confirmPassword"
          label={lang["confirmPassword"][locale]}
          placeholder={lang["placeholder-confirmPassword"][locale]}
        />

        <FormField
          control={typedForm.control}
          name="accept"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-y-0">
              <FormControl>
                <Checkbox
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="flex items-center space-x-1">
                <label
                  htmlFor={field.name}
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {lang["agree-to"][locale]}
                </label>
                <Link
                  to={localizedPath(locale, "/terms")}
                  className="text-sm text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {lang["terms-and-conditions"][locale]}
                </Link>
              </div>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="default"
          className="w-full py-6 h-12 mt-6"
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : lang["register"][locale]}
        </Button>
      </form>
    </Form>
  );
}

export default FormRegisterWithPhone;
